import React, { useEffect, useMemo, useState } from "react";
import { Task } from "@/bryntum-lib/models/Task";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Label } from "@/modern/components/ui/label";
import { Button } from "@/modern/components/ui/button";
import { Badge } from "@/modern/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { taskSchema, TaskFormData } from "@/react/schema/taskSchema";
import { ColorPicker } from "@/modern/components/color-picker";
import { eventStyles, STRIPED_TASK_STYLE } from "@/other/constants/TaskTypes";
import { TaskStylePreview } from "@/react/components/entityViews/contextMenus/task/TaskStylePreview";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getNewTaskColor } from "@/other/utils/bgUtils";
import TaskEditorColorMapping from "@/other/constants/TaskEditorColors";
import type { ISwivuStore } from "@/types/SwivuStore";
import { toast } from "sonner";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { ENTITY_CONFIGS } from "@/other/constants/EntityManagerConfig";
import { SwivuDuplicateEntityHelper } from "@/react/components/SwivuDuplicateEntityHelper";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { EntityPillBar } from "@/react/components/entityManager/components/EntityPillBar";
import { Loader2 } from "lucide-react";
import { useConfirm } from "@omit/react-confirm-dialog";

interface TaskGeneralEditorProps {
  record: {
    task: Task;
    onAfterRecordAdd?: (taskRecord: Task) => void;
  };
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const getRecentTaskByLimit = (store: ISwivuStore<Task>, limit = 6): Task[] => {
  return store
    .query(() => true, true)
    .sort((recordA: Task, recordB: Task) => {
      const dateA = recordA.createdAt ? new Date(recordA.createdAt).getTime() : 0;
      const dateB = recordB.createdAt ? new Date(recordB.createdAt).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, limit);
};

const allStyles = [...eventStyles, STRIPED_TASK_STYLE];
const auditDateOptions: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

const formatAuditDate = (dateValue?: string | number | Date): string => {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);
  return isNaN(parsedDate.getTime()) ? "" : parsedDate.toLocaleDateString("en-US", auditDateOptions);
};

export const TaskGeneralEditor = ({ record, onClose, editorInstance }: TaskGeneralEditorProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { project, entityManager } = useSwivuBryntumContext();
  const { task: taskRecord, onAfterRecordAdd } = record;
  const [stylePickerOpen, setStylePickerOpen] = useState(false);
  const { subscribe } = useSwivuStore<Task>({ storeId: STORE_KEYS.taskStore });
  const confirm = useConfirm();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    getValues,
    formState: { errors, isDirty, isValid },
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      name: taskRecord.name || "",
      agendaTitle: taskRecord.agendaTitle || "",
      description: taskRecord.description || "",
      color: taskRecord.color || "#FF8A65",
      taskStyle: taskRecord.taskStyle || eventStyles[0],
      statusId: taskRecord.statusId || null,
      categoryId: taskRecord.categoryId || null,
      teamId: taskRecord.teamId || null,
      phaseId: taskRecord.phaseId || null,
      tagsId: taskRecord.tagsId || null,
      clientId: taskRecord.clientId || null,
      budgetId: taskRecord.budgetId || null
    }
  });

  const currentColor = watch("color");
  const currentStyle = watch("taskStyle");

  useEffect(() => {
    if (taskRecord.isPhantom) {
      const recentCreatedTasks = getRecentTaskByLimit(project.extraTaskStore);
      const recentTasksColors = recentCreatedTasks.map((record) => record.color);
      const { color } = getNewTaskColor(TaskEditorColorMapping, recentTasksColors);
      reset({
        ...getValues(),
        color
      });
    }
    return subscribe(({ record: changedRecord }) => {
      if (taskRecord.id === changedRecord.id) {
        reset({
          name: changedRecord.name || "",
          agendaTitle: changedRecord.agendaTitle || "",
          description: changedRecord.description || "",
          color: changedRecord.color || "#FF8A65",
          taskStyle: changedRecord.taskStyle || eventStyles[0],
          statusId: changedRecord.statusId || null,
          categoryId: changedRecord.categoryId || null,
          teamId: changedRecord.teamId || null,
          phaseId: changedRecord.phaseId || null,
          tagsId: changedRecord.tagsId || null,
          clientId: changedRecord.clientId || null,
          budgetId: changedRecord.budgetId || null
        });
        return true;
      }
      return false;
    });
  }, [taskRecord]);

  const onSubmit = async (data: TaskFormData) => {
    try {
      setIsSaving(true);
      taskRecord.set(data);
      const isNewTask = taskRecord.isPhantom;
      if (isNewTask) {
        project.extraTaskStore.add(taskRecord);
        onAfterRecordAdd?.(taskRecord);
      }

      await project.sync();
      toast.success("Task saved successfully");
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Something went wrong while saving the task. Please try again.", {
        description: "If the problem persists, please contact support."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        alertDialogOverlay: {
          className: "z-[--swivu-bryntum-z-index]"
        },
        alertDialogContent: {
          className: "z-[--swivu-bryntum-z-index]"
        },
        customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">
              Keep working
            </Button>
            <Button variant="destructive" onClick={confirm} size="sm">
              Do not save
            </Button>
            <Button
              variant="success"
              onClick={async () => {
                if (!isValid) {
                  await trigger("name");
                  cancel();
                  return;
                }
                handleSave();
                confirm();
              }}
              size="sm"
            >
              Save & close
            </Button>
          </div>
        )
      });

      if (confirmed) onClose();
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    await handleSubmit(onSubmit)();
  };

  const openDashboard = () => {
    if (entityManager) {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id });
    }
  }

  const handleSaveAndDashboard = async () => {
    await handleSave();
    if (isValid) {
      openDashboard();
      onClose();
    }
  };

  const hideDashboardButton = entityManager.isOpen && entityManager.entityId == taskRecord.id;

  const taskMetaItems = useMemo(() => {
    const items: Array<{ key: string; label: string; value: string }> = [];

    if (taskRecord.creatorRecord?.name && taskRecord.createdAt) {
      items.push({
        key: "created",
        label: "Created",
        value: `${taskRecord.creatorRecord.name} - ${formatAuditDate(taskRecord.createdAt)}`
      });
    }

    if (taskRecord.updaterRecord?.name && taskRecord.updatedAt) {
      items.push({
        key: "updated",
        label: "Updated",
        value: `${taskRecord.updaterRecord.name} - ${formatAuditDate(taskRecord.updatedAt)}`
      });
    }

    if (taskRecord.lastEventUpdatedByRecord?.name && taskRecord.lastEventUpdatedAt) {
      items.push({
        key: "scheduled",
        label: "Scheduled",
        value: `${taskRecord.lastEventUpdatedByRecord.name} - ${formatAuditDate(taskRecord.lastEventUpdatedAt)}`
      });
    }

    return items;
  }, [taskRecord.createdAt, taskRecord.creatorRecord?.name, taskRecord.updatedAt, taskRecord.updaterRecord?.name, taskRecord.lastEventUpdatedAt, taskRecord.lastEventUpdatedByRecord?.name]);

  const headerRenderer = () => (
    <div className="drag-handle flex items-center justify-between p-5 border-b border-border/50 rounded-t-lg border-t-2" style={{ borderTopColor: currentColor }}>
      <h2 className="text-lg m-0">General Settings</h2>
      <button
        onClick={handleClose}
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100 !size-12"
      >
        <i className="fa-light fa-times text-swivu-icon" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  return (
    <BaseModal
      record={taskRecord}
      onClose={handleClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "66rem",
        height: "41.8rem",
        maxHeight: "90vh",
        maxWidth: "90vw",
        minWidth: "66rem",
        minHeight: "41.8rem"
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      footerRenderer={() => {
        return (
          <div className="w-full">
            <div className="flex w-full flex-col gap-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-wrap items-center gap-1.5 text-[0.7rem] text-muted-foreground">
                {taskMetaItems.length ? (
                  taskMetaItems.map((item) => (
                    <div key={item.key} className="flex items-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-2.5 py-1.5 text-left">
                      <Badge variant="outline" className="rounded-full border-transparent bg-muted/50 px-2 py-0 text-[0.55rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        {item.label}
                      </Badge>
                      <span className="font-medium text-foreground/90">{item.value}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground/70">No timeline activity yet.</span>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                {isSaving && (
                  <div className="flex items-center gap-2 text-muted-foreground/70 text-sm mr-3">
                    Saving Changes <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                )}
                <Button onClick={handleSave} disabled={!isDirty || isSaving}>
                  Save Changes
                </Button>
                {!hideDashboardButton && (
                  <Button type="button" disabled={(taskRecord.isPhantom && !isDirty) || isSaving} onClick={() => {
                    /* If no changes in form & not phantom then directly open dashboard */
                    if (!isDirty && !taskRecord.isPhantom) {
                      openDashboard();
                      onClose();
                    } else {
                      handleSaveAndDashboard();
                    }
                  }}>
                    Open Dashboard <i className="fa-solid fa-arrow-right -rotate-45" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }}
      headerRenderer={headerRenderer}
      headerIconCls=""
      className={`bg-background resize-none overflow-visible border-0 ${editorInstance?.className || ""}`}
    >
      <BaseModalBody contentElementClassName="bg-transparent" className="!p-0">
        <form className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {/* Info Message */}
            <div className="w-full flex items-start gap-3 text-sm rounded-md py-2.5 px-3.5  border-primary bg-card ">
              <i className="fa-solid fa-circle-info mt-0.5 flex-shrink-0 text-primary"></i>
              <span className="text-foreground/90">Tasks can be jobs, projects, assignments, actions, goals, chores, lessons, responsibilities, activities, and more.</span>
            </div>

            <EntityPillBar
              record={taskRecord}
              entityConfig={ENTITY_CONFIGS[ENTITY_TYPES.TASK]}
              contentElementClassName="gap-x-8 gap-y-4 flex-wrap"
              isDirectUpdates={false}
              onFieldChange={(changes) => {
                setValue(changes.fieldName as keyof TaskFormData, changes.value, { shouldDirty: true });
              }}
              isFieldDisabled={isSaving}
              className="px-0"
              noScrollArea={true}
              itemElementClassName="flex-col justify-start items-start"
              wrapperElementClassName="p-4 bg-card rounded-md"
            />

            {/* Task Details Section */}
            <div className="space-y-6 p-4 bg-card rounded-md">
              {/* Name Fields - 2 Column Layout */}
              <div className="grid grid-cols-2 gap-6 ">
                {/* Internal Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Internal Name *</Label>
                  <Controller
                    name="name"
                    control={control}
                    disabled={isSaving}
                    render={({ field }) => {
                      const DuplicateHelperComponent = SwivuDuplicateEntityHelper as React.ComponentType<{
                        record: Task;
                        store: ISwivuStore<Task>;
                        placeholder: string;
                        editorReactState: unknown;
                        onChange: (params: { value: string; record?: Task }) => void;
                        disabled: boolean;
                        editorRecordKey: string;
                        inputElementClassName: string;
                        name: string;
                        value: string;
                        onBlur: () => void;
                      }>;
                      return (
                        <DuplicateHelperComponent
                          {...field}
                          record={taskRecord}
                          store={project.extraTaskStore}
                          placeholder="Displayed on HQ and Workforce"
                          editorReactState={editorInstance}
                          onChange={({ value, record: selectedRecord }: { value: string; record?: Task }) => {
                            if (selectedRecord) {
                              // Reset form with all values from the selected existing task
                              reset({
                                name: selectedRecord.name || "",
                                agendaTitle: selectedRecord.agendaTitle || "",
                                description: selectedRecord.description || "",
                                color: selectedRecord.color || "#FF8A65",
                                taskStyle: selectedRecord.taskStyle || eventStyles[0],
                                statusId: selectedRecord.statusId || null,
                                categoryId: selectedRecord.categoryId || null,
                                teamId: selectedRecord.teamId || null,
                                phaseId: selectedRecord.phaseId || null,
                                tagsId: selectedRecord.tagsId || null,
                                clientId: selectedRecord.clientId || null,
                                budgetId: selectedRecord.budgetId || null
                              });

                              if (taskRecord.isPhantom) {
                                onAfterRecordAdd?.(selectedRecord);
                              }
                            } else {
                              // Just update the name field for new tasks
                              field.onChange(value);
                              if (taskRecord.isPhantom) {
                                setValue("agendaTitle", value, { shouldDirty: true });
                              }
                            }
                          }}
                          disabled={isSaving}
                          editorRecordKey="task"
                          inputElementClassName="flex w-full rounded-md text-base ring-offset-background  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-background h-swivu-input px-3 py-2"
                        />
                      );
                    }}
                  />
                  {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                  <p className="text-xs text-muted-foreground">Displayed on HQ and Workforce</p>
                </div>

                {/* External Name (Agenda Title) */}
                <div className="space-y-2">
                  <Label htmlFor="agendaTitle">External Name</Label>
                  <Controller
                    name="agendaTitle"
                    control={control}
                    disabled={isSaving}
                    render={({ field }) => (
                      <Input
                        id="agendaTitle"
                        variant="background"
                        placeholder="Displayed on Client and Public Schedules"
                        autoComplete="off"
                        {...field}
                        className={errors.agendaTitle ? "border-destructive" : ""}
                      />
                    )}
                  />
                  {errors.agendaTitle && <p className="text-sm text-destructive">{errors.agendaTitle.message}</p>}
                  <p className="text-xs text-muted-foreground">Displayed on Client and Public Schedules</p>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Controller
                  name="description"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => (
                    <Textarea
                      id="description"
                      placeholder="Add a detailed description..."
                      autoComplete="off"
                      {...field}
                      className={`min-h-[120px] resize-y ${errors.description ? "border-destructive" : ""}`}
                    />
                  )}
                />
                {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
                <p className="text-xs text-muted-foreground">Provide additional context or details about this task.</p>
              </div>

              {/* Appearance Section */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  {/* Task Color */}
                  <div className="space-y-2">
                    <Label>Task Color</Label>
                    <ColorPicker
                      value={currentColor}
                      onChange={(color) => {
                        if (color) {
                          setValue("color", color, { shouldDirty: true });
                        }
                      }}
                      editable={false}
                      className="border-0 shadow-none"
                    >
                      <Button disabled={isSaving} type="button" variant="outline" className="w-full justify-start h-swivu-input">
                        <div className="mr-2 h-5 w-5 rounded border" style={{ backgroundColor: currentColor || "transparent" }} />
                        {currentColor || "Select color"}
                      </Button>
                    </ColorPicker>
                    <p className="text-xs text-muted-foreground">Choose a color for your task</p>
                  </div>

                  {/* Task Style */}
                  <div className="space-y-2">
                    <Label>Task Style</Label>
                    <Popover open={stylePickerOpen} onOpenChange={setStylePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-start h-swivu-input" disabled={isSaving}>
                          <i className="fa-light fa-palette mr-2" />
                          {currentStyle.charAt(0).toUpperCase() + currentStyle.slice(1)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                        <div className="space-y-1">
                          {allStyles.map((style) => (
                            <button
                              key={style}
                              type="button"
                              disabled={isSaving}
                              onClick={() => {
                                setValue("taskStyle", style, { shouldDirty: true });
                                setStylePickerOpen(false);
                              }}
                              className="w-full p-2 rounded hover:bg-accent transition-colors"
                            >
                              <TaskStylePreview style={style} isSelected={currentStyle === style} color={currentColor} />
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">Select a style for your task</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
};
